import { z } from "zod";
import { Created, Id, IsoDate, Minor, Ok } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

export const PromoState = z.enum(["live", "upcoming", "expired"]);
export type PromoState = z.infer<typeof PromoState>;

export const PriceHistoryItem = z.object({
  id: Id,
  visitPriceMinor: Minor,
  effectiveFrom: z.string(),
});

export const PackageItem = z.object({
  id: Id,
  specialistId: Id,
  title: z.string(),
  visitCount: z.number().int(),
  discountPct: z.number().int(),
  /** Computed from the specialist's current visit price; null until a price is set. */
  priceMinor: Minor.nullable(),
});
export type PackageItem = z.infer<typeof PackageItem>;

export const PromoItem = z.object({
  id: Id,
  title: z.string(),
  discountPct: z.number().int(),
  startsOn: IsoDate,
  endsOn: IsoDate,
  specialistId: Id.nullable(),
  specialistName: z.string().nullable(),
  state: PromoState,
});
export type PromoItem = z.infer<typeof PromoItem>;

export const SpecialistPricing = z.object({
  specialistId: Id,
  specialistName: z.string(),
  currentPriceMinor: Minor.nullable(),
  effectiveFrom: z.string().nullable(),
  history: z.array(PriceHistoryItem),
  packages: z.array(PackageItem),
});
export type SpecialistPricing = z.infer<typeof SpecialistPricing>;

/** The /pricing screen: every specialist for managers/admins, only their own card for a specialist. */
export const PricingBoard = z.object({
  specialists: z.array(SpecialistPricing),
  promos: z.array(PromoItem),
});
export type PricingBoard = z.infer<typeof PricingBoard>;

export const PromoOption = z.object({ id: Id, title: z.string(), discountPct: z.number().int() });

/** Base price in force + the best live promo on the Kyiv date (the one discount resolver). */
export const VisitQuote = z.object({
  baseMinor: Minor,
  discountPct: z.number().int(),
  promo: PromoOption.nullable(),
  finalMinor: Minor,
});
export type VisitQuote = z.infer<typeof VisitQuote>;

export const SetVisitPriceBody = z.object({ specialistId: Id, priceMinor: Minor });
export type SetVisitPriceBody = z.infer<typeof SetVisitPriceBody>;

/** Business bounds (2–100 visits, 0–100 %) are domain rules → 422 with a code, not 400. */
export const SavePackageBody = z.object({
  id: Id.optional(),
  specialistId: Id,
  title: z.string().max(120),
  visitCount: z.number().int(),
  discountPct: z.number().int(),
});
export type SavePackageBody = z.infer<typeof SavePackageBody>;

export const SavePromoBody = z.object({
  id: Id.optional(),
  /** null / omitted = clinic-wide promo. */
  specialistId: Id.nullable().optional(),
  title: z.string().max(120),
  discountPct: z.number().int(),
  startsOn: IsoDate,
  endsOn: IsoDate,
});
export type SavePromoBody = z.infer<typeof SavePromoBody>;

export const pricing = defineRoutes({
  board: {
    method: "GET",
    path: "/pricing",
    summary: "Price list: specialists (prices, history, package definitions) and promos",
    responses: { 200: PricingBoard, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  quote: {
    method: "GET",
    path: "/pricing/quote",
    summary: "Quote one visit for a specialist at an instant (default: now)",
    query: z.object({ specialistId: Id, at: z.string().datetime({ offset: true }).optional() }),
    responses: { 200: VisitQuote, 401: PROBLEMS[401], 403: PROBLEMS[403], 422: PROBLEMS[422] },
  },
  setVisitPrice: {
    method: "POST",
    path: "/pricing/visit-prices",
    summary:
      "Append a new effective-dated visit price for a specialist (history is never rewritten)",
    body: SetVisitPriceBody,
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  savePackage: {
    method: "POST",
    path: "/pricing/packages",
    summary: "Create or update a package definition",
    body: SavePackageBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  archivePackage: {
    method: "POST",
    path: "/pricing/packages/:packageId/archive",
    summary: "Soft-archive a package definition (sold packages keep working)",
    params: z.object({ packageId: Id }),
    responses: { 200: Ok, 401: PROBLEMS[401], 403: PROBLEMS[403], 422: PROBLEMS[422] },
  },
  savePromo: {
    method: "POST",
    path: "/pricing/promos",
    summary: "Create or update a promo (clinic-wide or per specialist)",
    body: SavePromoBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  archivePromo: {
    method: "POST",
    path: "/pricing/promos/:promoId/archive",
    summary: "Soft-archive a promo",
    params: z.object({ promoId: Id }),
    responses: { 200: Ok, 401: PROBLEMS[401], 403: PROBLEMS[403], 422: PROBLEMS[422] },
  },
});
